import React, { useCallback } from 'react';

import { getDepartments } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';
import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Input } from '@vlabs/uikit/components/controls/input/Input';
import type { TOption } from '@vlabs/uikit/types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './ListFormCreate.module.sass';

interface ListFormCreateProps {
  defaultValues?: {
    user_data?: string;
    departmentId?: DepartmentId;
  };
  initialDepartmentId?: DepartmentId;
  onCancel?: VoidFunction;
  onSubmit: (values: { user_data: string; departmentId: DepartmentId }) => void;
}

function ListFormCreate({
  defaultValues = undefined,
  initialDepartmentId = '',
  onCancel = undefined,
  onSubmit,
}: ListFormCreateProps): React.JSX.Element {
  const { t } = useTranslation();

  const form = useForm<{
    user_data: string;
    departmentId: DepartmentId;
  }>({
    defaultValues: {
      user_data: defaultValues?.user_data ?? '',
      departmentId: defaultValues?.departmentId ?? initialDepartmentId,
    },
  });

  const loadOptions = useCallback(async (inputValue: string): Promise<TOption[]> => {
    const { departments = [] } = await getDepartments({ ...(inputValue ? { name: inputValue } : {}) });
    return departments.map((department) => ({
      value: department.department_id,
      label: department.name ?? department.department_id,
    }));
  }, []);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={st.Root}
        data-testid="listFormCreate"
      >
        <h5>{t('list-list-page:form.title_create')}</h5>

        <div className={st.Fields}>
          <div className={st.InputWrapper}>
            <Input
              id="user_data"
              label={t('list-list-page:form.fields.name.label')}
              placeholder={t('list-list-page:form.fields.name.placeholder')}
              {...form.register('user_data', {
                required: validate.required(),
                maxLength: validate.maxLength(128),
              })}
              errors={form.formState.errors}
            />
          </div>

          {!initialDepartmentId && (
            <div>
              <ControllableValueSelect
                cacheOptions
                control={form.control}
                defaultOptions
                errors={form.formState.errors}
                id="departmentId"
                isAsync
                isClearable
                label={t('list-list-page:form.fields.department.label')}
                loadOptions={loadOptions}
                name="departmentId"
                noOptionsMessage={() => t('list-list-page:form.fields.department.empty')}
                placeholder={t('list-list-page:form.fields.department.placeholder')}
                rules={{ required: validate.required() }}
              />
            </div>
          )}
        </div>

        <div className={st.Actions}>
          <Button
            className={st.Button}
            data-testid="listFormCreate.submitButton"
            type="submit"
          >
            {t('list-list-page:form.submit_create')}
          </Button>

          <Button
            className={st.Button}
            data-testid="listFormCreate.cancelButton"
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
          >
            {t('list-list-page:form.cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

export { ListFormCreate };
